import { type Dispatch, Fragment, type JSX, type SetStateAction, useRef } from 'react';
import { cx } from '@linaria/core';

import { REPUBS } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { type Repub } from '@cx-modules/types';

import { getGroupTitle } from '../../helpers';
import { useMatchesCouponContext } from '../../hooks';
import { type CustomisableComponentProps } from '../../types';

import { useAlignBetsWithLabels } from './hooks';
import { Label, LabelDesktop, Labels, MobileTitle, Title, TitleWrapper } from './style';

interface GroupTitleProps extends CustomisableComponentProps {
  dateIsoString: string;
  marketLabels: { betTypeId: number; name: string }[];
  matchStatsEnabled?: boolean;
  repub: Repub;
  setBetsWrapperWidth: Dispatch<SetStateAction<number>>;
  tabletUpMarginRight: number;
}

export const GroupTitle = ({
  className,
  dateIsoString,
  marketLabels,
  matchStatsEnabled = true,
  repub,
  setBetsWrapperWidth,
  style = {},
  tabletUpMarginRight,
}: GroupTitleProps): JSX.Element => {
  const timeZone = useMatchesCouponContext(state => state.timeZone);

  const labelsRef = useRef<HTMLDivElement>(null);
  useAlignBetsWithLabels({
    labelsRef,
    setBetsWrapperWidth,
  });

  const { t } = useTranslation('bettype');

  const title = getGroupTitle({
    dateIsoString,
    dateTimeFormat: REPUBS[repub].dateProperties.format.dateFull,
    repub,
    timeZone,
  });

  const mobileTitle = getGroupTitle({
    dateIsoString,
    dateTimeFormat: REPUBS[repub].dateProperties.format.dateShort,
    repub,
    timeZone,
  });

  return (
    <TitleWrapper className={cx(className)} matchStatsEnabled={matchStatsEnabled} style={style}>
      <Title data-testid="group-title">{title}</Title>
      <MobileTitle data-testid="group-title-mobile">{mobileTitle}</MobileTitle>
      <Labels ref={labelsRef} tabletUpMarginRight={tabletUpMarginRight}>
        {marketLabels.map(l => {
          const splits = l.name.split('-');
          return (
            <Fragment key={l.name}>
              <LabelDesktop>{t(l.name)}</LabelDesktop>
              <Label>
                {splits.map((s, index, array) => (
                  <span key={s}>
                    {t(s, { ns: 'bettype' })}
                    {index === 0 && array.length > 1 && '-'}
                  </span>
                ))}
              </Label>
            </Fragment>
          );
        })}
        {marketLabels.length < 3 && <Label />}
      </Labels>
    </TitleWrapper>
  );
};
